import type {
  CommitmentStateT,
  ImportanceT,
  Initiative,
  InitiativeLifecycleT,
} from '@portfolio-pm/shared';
import { newId } from '@portfolio-pm/shared';
import * as Y from 'yjs';
import {
  type WithNow,
  getDeletedMap,
  getInitiativesMap,
  getObjectivesMap,
  getProjectsMap,
} from './doc.ts';

export interface CreateInitiativeInput {
  projectId: string;
  title: string;
  description?: string;
  successDefinition?: string;
  lifecycle?: InitiativeLifecycleT;
  commitmentState?: CommitmentStateT;
  importance?: ImportanceT;
  reviewAt?: number;
  reviewTrigger?: string;
  minimumProgressCadenceDays?: number;
  objectiveIds?: string[];
}

export type UpdateInitiativeInput = {
  [K in keyof CreateInitiativeInput]?: CreateInitiativeInput[K] | undefined;
};

function timestamp(options: WithNow): number {
  return options.now ?? Date.now();
}

export function getInitiative(doc: Y.Doc, id: string): Initiative | undefined {
  const inner = getInitiativesMap(doc).get(id);
  return inner ? initiativeFromMap(id, inner) : undefined;
}

export function listInitiatives(doc: Y.Doc): Initiative[] {
  return [...getInitiativesMap(doc).entries()]
    .map(([id, inner]) => initiativeFromMap(id, inner))
    .sort((a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id));
}

export function createInitiative(
  doc: Y.Doc,
  input: CreateInitiativeInput,
  options: WithNow = {},
): Initiative {
  validateInitiativeInput(doc, input);
  const now = timestamp(options);
  const id = newId();
  const inner = new Y.Map<unknown>();
  inner.set('projectId', input.projectId);
  inner.set('title', input.title);
  inner.set('lifecycle', input.lifecycle ?? 'planned');
  inner.set('objectiveIds', [...new Set(input.objectiveIds ?? [])]);
  inner.set('createdAt', now);
  inner.set('updatedAt', now);
  setOptional(inner, 'description', input.description);
  setOptional(inner, 'successDefinition', input.successDefinition);
  setOptional(inner, 'commitmentState', input.commitmentState);
  setOptional(inner, 'importance', input.importance);
  setOptional(inner, 'reviewAt', input.reviewAt);
  setOptional(inner, 'reviewTrigger', input.reviewTrigger);
  setOptional(inner, 'minimumProgressCadenceDays', input.minimumProgressCadenceDays);
  getInitiativesMap(doc).set(id, inner);
  return initiativeFromMap(id, inner);
}

export function updateInitiative(
  doc: Y.Doc,
  id: string,
  patch: UpdateInitiativeInput,
  options: WithNow = {},
): void {
  const inner = getInitiativesMap(doc).get(id);
  if (!inner) return;
  const current = initiativeFromMap(id, inner);
  const candidate = {
    projectId: patch.projectId ?? current.projectId,
    title: patch.title ?? current.title,
    minimumProgressCadenceDays:
      patch.minimumProgressCadenceDays ?? current.minimumProgressCadenceDays,
    objectiveIds: patch.objectiveIds ?? current.objectiveIds,
  };
  validateInitiativeInput(doc, candidate);
  const now = timestamp(options);
  doc.transact(() => {
    for (const [key, value] of Object.entries(patch)) {
      if (key === 'objectiveIds') {
        inner.set(key, [...new Set(Array.isArray(value) ? value : [])]);
      } else if (value === undefined) {
        inner.delete(key);
      } else {
        inner.set(key, value);
      }
    }
    inner.set('updatedAt', now);
  });
}

export function deleteInitiative(doc: Y.Doc, id: string, options: WithNow = {}): void {
  if (!getInitiativesMap(doc).has(id)) return;
  const now = timestamp(options);
  doc.transact(() => {
    getInitiativesMap(doc).delete(id);
    getDeletedMap(doc).set(id, now);
  });
}

function validateInitiativeInput(
  doc: Y.Doc,
  input: {
    projectId: string;
    title: string;
    minimumProgressCadenceDays?: number | undefined;
    objectiveIds?: string[] | undefined;
  },
): void {
  if (!getProjectsMap(doc).has(input.projectId)) throw new Error('project not found');
  if (!input.title.trim()) throw new Error('initiative title is required');
  if (
    input.minimumProgressCadenceDays !== undefined &&
    (!Number.isInteger(input.minimumProgressCadenceDays) || input.minimumProgressCadenceDays <= 0)
  ) {
    throw new Error('minimumProgressCadenceDays must be a positive integer');
  }
  for (const objectiveId of new Set(input.objectiveIds ?? [])) {
    const objective = getObjectivesMap(doc).get(objectiveId);
    if (!objective) throw new Error(`objective ${objectiveId} not found`);
    if (objective.get('projectId') !== input.projectId) {
      throw new Error('initiative objectives must belong to the same project');
    }
  }
}

function initiativeFromMap(id: string, inner: Y.Map<unknown>): Initiative {
  const rawObjectiveIds = inner.get('objectiveIds');
  const initiative: Initiative = {
    id,
    projectId: String(inner.get('projectId') ?? ''),
    title: String(inner.get('title') ?? ''),
    lifecycle: (inner.get('lifecycle') ?? 'planned') as InitiativeLifecycleT,
    objectiveIds: Array.isArray(rawObjectiveIds) ? [...(rawObjectiveIds as string[])] : [],
    createdAt: Number(inner.get('createdAt') ?? 0),
    updatedAt: Number(inner.get('updatedAt') ?? 0),
  };
  copyString(inner, initiative, 'description');
  copyString(inner, initiative, 'successDefinition');
  copyValue(inner, initiative, 'commitmentState');
  copyValue(inner, initiative, 'importance');
  copyNumber(inner, initiative, 'reviewAt');
  copyString(inner, initiative, 'reviewTrigger');
  copyNumber(inner, initiative, 'minimumProgressCadenceDays');
  return initiative;
}

function setOptional(inner: Y.Map<unknown>, key: string, value: unknown): void {
  if (value !== undefined) inner.set(key, value);
}

function copyValue<T extends object>(inner: Y.Map<unknown>, target: T, key: keyof T): void {
  const value = inner.get(String(key));
  if (value !== undefined) (target as Record<string, unknown>)[String(key)] = value;
}

function copyString<T extends object>(inner: Y.Map<unknown>, target: T, key: keyof T): void {
  const value = inner.get(String(key));
  if (typeof value === 'string') (target as Record<string, unknown>)[String(key)] = value;
}

function copyNumber<T extends object>(inner: Y.Map<unknown>, target: T, key: keyof T): void {
  const value = inner.get(String(key));
  if (typeof value === 'number') (target as Record<string, unknown>)[String(key)] = value;
}
